import React, { useEffect, useState } from 'react';

/**
 * Agnostic left sidebar for the data viewers (GPS, Visits, …).
 *
 * Renders an optional `header` (typically a project CardSelect) above a vertical
 * navigation. It knows nothing about any specific viewer or context — everything
 * comes through props, so it lives in /ui and is shared.
 *
 * Props:
 *  - header?     : node rendered at the top (e.g. the project selector)
 *  - showNav     : whether to render the nav (e.g. only once a project is picked)
 *  - tabs        : Array<{ id, label, icon?, title? }> — `title` is a plain-string
 *                  tooltip, needed when `label` is a node (e.g. carries a badge)
 *  - activeTab   : currently active tab id
 *  - onTabChange : (id) => void
 *  - buildHref   : (id) => string — canonical href per item so cmd/ctrl-click opens
 *                  the section in a new tab and anchors stay keyboard-focusable
 *  - navLabel?   : aria-label for the <nav>
 *  - accent?     : { item, icon, ring? } Tailwind class strings for the active
 *                  state; `ring` is the focus-visible ring, accent-coloured
 *  - collapsible?: opt-in icons-only mode with a toggle. Off by default so the
 *                  viewers that never asked for it are untouched.
 *  - storageKey? : remembers the collapsed state per viewer, across sessions
 *
 * Sub-navigation (opt-in): a tab may declare `subItems: Array<{ id, label }>` —
 * the sections of a page, rendered indented under that tab while it is active.
 *  - activeSubItem?   : currently active sub-item id
 *  - onSubItemChange? : (id) => void
 *  - buildSubHref?    : (id) => string — canonical href per sub-item
 * Hidden in collapsed mode: an icons-only rail has no room for section names.
 */
export const ACCENT_PURPLE = {
  item: 'bg-[#f3f1fe] dark:bg-[#2a2440] text-[#5b3df0] dark:text-[#a98ff5] font-semibold',
  icon: 'text-[#5b3df0] dark:text-[#a98ff5]',
  ring: 'focus-visible:ring-[#5b3df0]',
};

export const ACCENT_BLUE = {
  item: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 font-semibold',
  icon: 'text-blue-600 dark:text-blue-300',
  ring: 'focus-visible:ring-blue-500',
};

// A convenience, not state we own: a browser that refuses storage (private mode,
// blocked site data) must still render a working sidebar.
const readStored = (key) => {
  if (!key) return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

// Sub-menus of the active tab, indented under it behind a hairline rule — sections
// of a page, not siblings of it. `buildHref` keeps cmd/ctrl-click and keyboard
// focus working, exactly like the tabs above.
const SubNav = ({ items, activeId, onSelect, buildHref, accent }) => (
  <div className="ml-5 mt-0.5 flex flex-col gap-0.5 border-l border-gray-200 pl-2 dark:border-gray-800">
    {items.map((item) => {
      const active = item.id === activeId;
      return (
        <a
          key={item.id}
          href={buildHref ? buildHref(item.id) : undefined}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            onSelect?.(item.id);
          }}
          aria-current={active ? 'true' : undefined}
          className={`flex items-center rounded-lg px-2.5 py-[7px] text-[12.5px] font-medium outline-none transition-colors focus-visible:ring-2 ${accent.ring || ''} ${
            active
              ? accent.item
              : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800/60'
          }`}
        >
          <span className="flex-1 truncate text-left">{item.label}</span>
        </a>
      );
    })}
  </div>
);

const ViewerSidebar = ({
  header = null,
  showNav = true,
  tabs = [],
  activeTab,
  onTabChange,
  buildHref,
  navLabel = 'Navigation',
  accent = ACCENT_PURPLE,
  collapsible = false,
  storageKey = null,
  activeSubItem = null,
  onSubItemChange = null,
  buildSubHref = null,
}) => {
  const [collapsed, setCollapsed] = useState(() => collapsible && readStored(storageKey));

  useEffect(() => {
    if (!collapsible || !storageKey) return;
    try {
      window.localStorage.setItem(storageKey, collapsed ? '1' : '0');
    } catch {
      // Nothing to do: the sidebar just forgets between sessions.
    }
  }, [collapsed, collapsible, storageKey]);

  const handleClick = (e, tabId) => {
    // Let the browser handle modified clicks (new tab / new window / etc.)
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    onTabChange?.(tabId);
  };

  const href = (id) => (buildHref ? buildHref(id) : undefined);
  // Never collapsed while there is no nav to collapse TO: the header holds the
  // project selector, so a remembered collapsed state would greet the user with
  // a single chevron and a page telling them to pick a project.
  const isCollapsed = collapsible && collapsed && showNav;

  const tooltipFor = (tab) => tab.title || (typeof tab.label === 'string' ? tab.label : undefined);

  return (
    <div
      className={`${isCollapsed ? 'w-16 px-2 py-3' : 'w-64 p-3 sm:p-4 lg:p-5'} bg-white dark:bg-[#1a1a1a] border-r border-gray-200 dark:border-gray-900 h-full overflow-y-auto flex flex-col gap-4 ${
        collapsible ? 'overflow-x-hidden transition-[width] duration-150' : ''
      }`}
    >
      {/* The project selector needs room to be readable; collapsed, the icons are
          the whole point, so it steps aside rather than being squeezed. */}
      {!isCollapsed && header}

      {collapsible && showNav && (
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!isCollapsed}
          aria-controls="viewer-sidebar-nav"
          aria-label={isCollapsed ? 'Déplier le menu' : 'Replier le menu'}
          title={isCollapsed ? 'Déplier le menu' : 'Replier le menu'}
          className={`flex items-center gap-2 rounded-lg px-2 py-2 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 ${
            isCollapsed ? 'justify-center' : 'self-end'
          }`}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`w-4 h-4 transition-transform ${isCollapsed ? 'rotate-180' : ''}`}
            aria-hidden
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          {!isCollapsed && <span>Replier</span>}
        </button>
      )}

      {showNav && (
        <nav id="viewer-sidebar-nav" className="space-y-1" aria-label={navLabel}>
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <React.Fragment key={tab.id}>
              <a
                href={href(tab.id)}
                onClick={(e) => handleClick(e, tab.id)}
                aria-current={isActive ? 'page' : undefined}
                title={isCollapsed ? tooltipFor(tab) : undefined}
                className={`flex items-center w-full gap-2 py-2.5 text-sm font-medium rounded-lg transition-all outline-none focus-visible:ring-2 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-[#1a1a1a] ${accent.ring || 'focus-visible:ring-blue-500'} ${
                  isCollapsed ? 'justify-center px-2' : 'px-3'
                } ${
                  isActive
                    ? accent.item
                    : 'text-gray-700 dark:text-gray-300 hover:bg-white/60 dark:hover:bg-gray-700/60 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                {tab.icon && (
                  <span className={`flex-shrink-0 ${isActive ? accent.icon : 'text-gray-500 dark:text-gray-400'}`} aria-hidden>
                    {React.cloneElement(tab.icon, { className: 'w-4 h-4 sm:w-5 sm:h-5', strokeWidth: '2' })}
                  </span>
                )}
                {/* Hidden, never removed: the label is this link's accessible name,
                    and a nav of unnamed icons is unusable with a screen reader. */}
                <span className={isCollapsed ? 'sr-only' : 'flex-1 text-left truncate'}>
                  {tab.label}
                </span>
                {isActive && !isCollapsed && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`w-4 h-4 opacity-70 flex-shrink-0 ${accent.icon}`}
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                )}
              </a>
                {isActive && !isCollapsed && tab.subItems?.length > 0 && (
                  <SubNav
                    items={tab.subItems}
                    activeId={activeSubItem}
                    onSelect={onSubItemChange}
                    buildHref={buildSubHref}
                    accent={accent}
                  />
                )}
              </React.Fragment>
            );
          })}
        </nav>
      )}
    </div>
  );
};

export default ViewerSidebar;
